import React from 'react';
import { styled, keyframes } from '@compiled/react';
import ErrorImage from '@atlaskit/icon/core/status-error';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { CardBaseStyle } from '../../../../../common/ui/styled.tsx';
import ErrorInfo from './error-info/index.tsx';
import messages from './messages.tsx';
import { errorWrapperSelector, skeletonBackgroundSelector, columnMaskSelector } from './styled.tsx';

const shimmer = keyframes({
	'0%': {
		backgroundPosition: '-300px 0',
	},
	'100%': {
		backgroundPosition: '1000px 0',
	},
});
// eslint-disable-next-line jira/styled/styled-component-order, @atlaskit/ui-styling-standard/no-styled
const SkeletonBackground = styled.div<{ hasError: boolean }>({
	animationDuration: '1.2s',
	animationFillMode: 'forwards',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	animationIterationCount: (props) => (props.hasError ? 0 : 'infinite'),
	animationName: shimmer,
	animationTimingFunction: 'linear',

	backgroundColor: token('color.skeleton.subtle'),
	backgroundImage: `linear-gradient(
        to right,
        ${token('color.skeleton.subtle')} 10%,
        ${token('color.skeleton')} 20%,
        ${token('color.skeleton.subtle')} 30%
    )`,
	backgroundRepeat: 'no-repeat',
	position: 'absolute',
	left: 0,
	right: 0,
	top: 0,
	bottom: 0,
});
const defaultIssueCardHeight = 97;
// eslint-disable-next-line jira/styled/styled-component-order, @atlaskit/ui-styling-standard/no-styled
const SkeletonContainer = styled.div({
	height: `${defaultIssueCardHeight}px`,
	display: 'flex',
	position: 'relative',
});

type Props = {
	issueIndex: number;
	hasError?: boolean;
};

const IssueCardSkeleton = ({ hasError = false, issueIndex }: Props) => {
	const { formatMessage } = useIntl();

	return (
		<Box
			as="li"
			xcss={isVisualRefreshEnabled() ? ListItemStyle : ListItemStyleOld}
			aria-label={formatMessage(
				fg('jira-issue-terminology-refresh-m3')
					? messages.issueLoadingLabelIssueTermRefresh
					: messages.issueLoadingLabel,
				{ number: issueIndex },
			)}
		>
			<CardContainer visualRefresh={isVisualRefreshEnabled()}>
				<SkeletonContainer>
					<SkeletonBackground
						hasError={hasError}
						data-component-selector={skeletonBackgroundSelector}
					/>
					<ColumnMask data-component-selector={columnMaskSelector}>
						<SummaryMask>
							<TextLineMask fontSize={20} lineHeight={24} intrusion={20} />
							<TextLineMask fontSize={20} lineHeight={24} intrusion={60} />
						</SummaryMask>
						<FooterMask>
							<TypeMask />
							<SeparatorMask />
							<IssueKeyMask />
							{hasError ? (
								<ErrorIconContainer>
									<ErrorImage spacing="spacious" color={token('color.icon.danger')} label="" />
								</ErrorIconContainer>
							) : (
								<BlankMask />
							)}
						</FooterMask>
					</ColumnMask>
					<ErrorWrapper data-component-selector={errorWrapperSelector}>
						<ErrorInfo />
					</ErrorWrapper>
				</SkeletonContainer>
			</CardContainer>
		</Box>
	);
};

export default IssueCardSkeleton;

const ListItemStyleOld = xcss({
	marginBlockStart: 'space.0',
});

const ListItemStyle = xcss({
	marginBlockStart: 'space.0',
	marginBlockEnd: 'space.050',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const CardContainer = styled(CardBaseStyle)({});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ErrorWrapper = styled.div({
	display: 'none',
	width: '100%',
	margin: 0,
	alignItems: 'center',
	marginLeft: token('space.100'),
	marginRight: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ErrorIconContainer = styled.div({
	backgroundColor: token('elevation.surface'),
	flexGrow: 1,
	display: 'flex',
	justifyContent: 'flex-end',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const BlankMask = styled.div({
	flexGrow: 1,

	backgroundColor: token('elevation.surface'),
	display: 'flex',
	justifyContent: 'flex-end',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const SeparatorMask = styled.div({
	width: '8px',

	backgroundColor: token('elevation.surface'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const IssueKeyMask = styled.div({
	position: 'relative',
	height: '24px',
	width: '96px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const TypeMask = styled.div({
	position: 'relative',
	height: '24px',
	width: '24px',
	marginRight: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const TextLineMask = styled.div<{ fontSize: number; intrusion: number; lineHeight: number }>({
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: `${(props: { fontSize: number }) => props.fontSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderRight: `${(props: { intrusion: number }) => props.intrusion}px solid ${token('color.border.inverse')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderTop: `${(props: { lineHeight: number; fontSize: number }) =>
		(props.lineHeight - props.fontSize) / 2}px solid ${token('color.border.inverse')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderBottom: `${(props: { lineHeight: number; fontSize: number }) =>
		(props.lineHeight - props.fontSize) / 2}px solid ${token('color.border.inverse')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const FooterMask = styled.div({
	display: 'flex',
	flexDirection: 'row',
	position: 'relative',

	borderBottom: `${token('border.width')} solid ${token('color.border.inverse')}`,
	borderTop: `16px solid ${token('color.border.inverse')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const SummaryMask = styled.div({
	borderBottom: `8px solid ${token('color.border.inverse')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ColumnMask = styled.div({
	position: 'relative',
	width: '100%',
	margin: 0,
	borderLeft: `8px solid ${token('color.border.inverse')}`,
	borderRight: `8px solid ${token('color.border.inverse')}`,
});
